import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DropEvent } from '../../../shared/external-libraries/ng-drag-drop/shared/drop-event.model';
import { MenuItem, TreeNode } from 'primeng/api';
import { interval, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, filter, switchMap, take, tap, timeout } from 'rxjs/operators';
import { DataCollection } from '../../../shared/models/site-content/data-collection.model';
import { FolderTree } from '../../../shared/models/folder-tree.model';
import { AccessibilityHelper } from '../../accessibility.helper';
import { AlertDialogComponent, AlertDialogModel } from '../../dialogs/alert/alert-dialog.component';
import { ConfirmationDialogComponent, ConfirmationDialogModel } from '../../dialogs/confirmation/confirmation-dialog.component';
import { BrowserStorageEnums } from '../../enums/browserstorage.enum';
import { ItemTypeEnum } from '../../enums/item-type.enum';
import { IDragDropItem } from '../../interfaces/drag-drop-items.interface';
import { IItem } from '../../interfaces/iitem.interface';
import { IListItem } from '../../interfaces/ilist-item.interface';
import { AsyncJob } from '../../models/async-job.model';
import { FolderFormInstanceTree } from '../../models/folder-form-instance-tree.model';
import { Folder } from '../../models/site-content/folder.model';
import { FormInstance } from '../../models/site-content/form-instance.model';
import { AsyncJobService } from '../../services/async-job.service';
import { BrowserStorageService } from '../../services/browserstorage.service';
import { ClipboardService } from '../../services/clipboard.service';
import { DragDropService } from '../../services/drag-drop.service';
import { FolderFormInstanceTreeService } from '../../services/folder-form-instance-tree.service';
import { FolderService } from '../../services/folder.service';
import { FormInstanceService } from '../../services/form-instance.service';
import { LoaderService } from '../../services/loader.service';
import { ProgressIndicatorService } from '../../services/progress-indicator.service';
import { RoleService } from '../../services/role.service';
import { UtilityHelper } from '../../utility.helper';
import { BaseViewComponent } from '../list-view/base-view/base-view.component';
import { DragDropEnum } from '../../enums/drag-drop.enum';
import { ListViewHelper } from '../list-view/list-view.helper';
import { all } from 'mathjs';
import { environment } from '../../../../environments/environment';
declare let $: any;

export interface FolderNode {
    type: string;
    id: number;
}

export class TreeConstants {
    public static readonly COPY = 'Copy';
    public static readonly CUT = 'Cut';
    public static readonly PASTE = 'Paste';
    public static readonly PASTE_ORIGINAL = 'Paste Copy of Original';
    public static readonly PASTE_FORM_REFERENCE = 'Paste Reference';
    public static readonly PASTE_ANOTHER_REFERENCE = 'Paste Another Reference';
    public static readonly DELETE = 'Delete';
    public static readonly ROLES = 'Roles';
    public static readonly ACCESS = 'Who can Access?';
    public static readonly EXPAND_ALL = 'Expand this Node';
    public static readonly COLLAPSE_ALL = 'Collapse this Node';
    public static readonly RENAME = 'Rename';
    public static readonly FOLDER_NODE = 'Folder';
    public static readonly FORM_NODE = 'FormInstance';
    public static readonly FORM_REF_NODE = 'FormInstanceRef';
    public static readonly REFERENCED_FORM_NODE = 'ReferencedFormInstance';
    public static readonly REGULAR_MODE = 'Regular';
    public static readonly PERMISSIONS_MODE = 'Permissions';
    public static readonly ICON_SOLID_CLASS = 'material-icons';
    public static readonly ICON_OUTLINE_CLASS = 'material-icons-outlined';
}
@Component({
    selector: 'app-folder-tree',
    templateUrl: './folder-tree.component.html',
    styleUrls: ['./folder-tree.component.scss'],
    standalone: false
})
export class FolderTreeComponent extends BaseViewComponent implements OnInit, OnChanges {
    // Properties.
    //private tree: FolderTree = null;
    private tree: FolderFormInstanceTree = null;
    public width: number = 400;

    public contextMenuItems: MenuItem[];
    private treeData: TreeNode[];

    // PrimeNG tree manages selectedNodes - when a node is selected, it adds it here
    private selectedNodes: TreeNode[] = [];
    // We manage copiedNodes - adding nodes to this when the user copies using the context menu
    public copiedNodes: TreeNode[]; // copied nodes
    // We manage cutNodes 
    public cutNodes: TreeNode[]; // cut nodes
    public rawFolderFormInstanceTree: FolderFormInstanceTree;
    public mode: string = TreeConstants.REGULAR_MODE;
    public permissionsLookupValue: string;
    public showingPermissionsFor: string;
    public refreshingTreeWithPermissions: boolean = false;
    public membersForPermissionsLookup: any[];
    private pinOutlineClass: string = TreeConstants.ICON_OUTLINE_CLASS;
    public includeFormsOutlineClass: string = TreeConstants.ICON_OUTLINE_CLASS;
    public modeClass: string = 'regular-mode';

    private resizeSubject = new Subject<any>();
    private debouncedResizeObservable = this.resizeSubject.pipe(debounceTime(10));
    private includeFormsKeyForStorage: string;
    private isLoading: boolean;
    private noData: boolean;

    @Input() site: DataCollection;
    @Input() rootFolderId: number;
    @Input() path: number[];
    @Input() treeIsStale: boolean = false;
    @Input() pinnedOpen: boolean = false;
    @Input() includeForms: boolean = false;

    @Output() nodeSelected: EventEmitter<any> = new EventEmitter();
    @Output() close: EventEmitter<any> = new EventEmitter();
    @Output() togglePinnedState: EventEmitter<any> = new EventEmitter();
    @Output() treeRequested: EventEmitter<any> = new EventEmitter();
    @Output() itemMoved: EventEmitter<any> = new EventEmitter<any>();


    public constructor(
        private progressIndicatorService: ProgressIndicatorService,
        private folderFormInstanceTreeService: FolderFormInstanceTreeService,
        private folderService: FolderService,
        private formInstanceService: FormInstanceService,
        private clipboardService: ClipboardService,
        private roleService: RoleService,
        private jobService: AsyncJobService,
        private loaderService: LoaderService,
        public dialog: MatDialog,
        public injectedDragDropService: DragDropService,
        private browserStorageService: BrowserStorageService) {
        super(dialog, injectedDragDropService);
    }

    // This is called both from the template on every width resize event, and from the debounced observable initialized in onNgInit.
    // When debounced is true it performs the actual calculation and sets this.width 
    public onWidthChange(event: any, debounced: boolean = false) {
        if (!debounced) {
            this.resizeSubject.next(event);
        } else {
            let sideNavCollapsed = this.browserStorageService.get(BrowserStorageEnums.SIDE_NAV_OPEN) == 'false';
            let sideNavOffset = sideNavCollapsed ? 60 : 250;
            let width = event.x - sideNavOffset;
            this.width = width;
        }
    }

    public ngOnInit(): void {
        this.isLoading = true;
        this.includeFormsKeyForStorage = `${BrowserStorageEnums.INCLUDE_FORMS_IN_TREE}_${this.site.id}`;
        let includeForms = this.browserStorageService.get(this.includeFormsKeyForStorage) === 'true';

        let treeObservable: Observable<FolderFormInstanceTree> = this.folderFormInstanceTreeService.getTreeAsObservable(this.site.id, includeForms);
        this.outputTreeRequested(treeObservable);
        treeObservable.subscribe(response => {
            this.tree = FolderFormInstanceTreeService.unpackTree(response);
            this.treeReceived(this.tree);
        });

        this.debouncedResizeObservable.subscribe(dragEvent => this.onWidthChange(dragEvent, true));

    }

    private outputTreeRequested(treeObservable: Observable<FolderFormInstanceTree>): void {
        let hshEventData = {
            treeComponent: this,
            treeObservable: treeObservable
        }
        this.treeRequested.emit(hshEventData);

        return;
    }

    // Sets treeData which is what NgPrime Tree component uses to display the tree
    private filterFormInstances(tree: FolderFormInstanceTree) {
        this.rawFolderFormInstanceTree = tree;
        this.treeData = this.rawTreeToTreeNodes(this.rawFolderFormInstanceTree);

        this.treeData = this.treeData.filter(x => x.children.filter(y => y.type.toUpperCase() == ItemTypeEnum.FOLDER.toUpperCase()));
    }

    private treeReceived(tree: FolderFormInstanceTree): void {
        if (!tree.childFolders) {
            this.noData = true;
        } else {
            this.noData = false;
        }
        this.filterFormInstances(tree);

        this.contextMenuItems = [
            { id: TreeConstants.RENAME, label: TreeConstants.RENAME, icon: 'pi pi-pencil', command: (e) => this.makeNodeEditable(e), disabled: false },
            { id: TreeConstants.CUT, label: TreeConstants.CUT, icon: 'pi pi-times', command: (e) => this.cutSelected(), disabled: false },
            { id: TreeConstants.COPY, label: TreeConstants.COPY, icon: 'pi pi-copy', command: (e) => this.copySelected(), disabled: false },
            { id: TreeConstants.PASTE, label: TreeConstants.PASTE, icon: 'pi pi-file', command: (e) => this.pasteIntoSelectedFolder(e, false), disabled: false },
            { id: TreeConstants.PASTE_FORM_REFERENCE, label: TreeConstants.PASTE_FORM_REFERENCE, icon: 'pi pi-file', command: (e) => this.pasteIntoSelectedFolder(e, true), disabled: false },
            { id: TreeConstants.DELETE, label: TreeConstants.DELETE, icon: 'pi pi-times-circle', command: (e) => this.deleteNode(e), disabled: false },
            { id: TreeConstants.EXPAND_ALL, label: TreeConstants.EXPAND_ALL, icon: 'pi pi-folder-open', command: (event) => this.handleContextMenuChoice(event), disabled: false },
            { id: TreeConstants.COLLAPSE_ALL, label: TreeConstants.COLLAPSE_ALL, icon: 'pi pi-folder', command: (event) => this.handleContextMenuChoice(event), disabled: false }
        ];
        this.path = this.path || [];
        if (this.path.length > 0) { this.expandPath(); }
        AccessibilityHelper.addAttribute('.ui-tree-filter', 'title', 'Filter Tree Input Field');
        this.setToggleButtonStates();
        this.isLoading = false;
    }

    ngOnChanges(changes: SimpleChanges) {
        let treeIsStale = changes.treeIsStale ? changes.treeIsStale.currentValue : false;
        if (treeIsStale) {
            this.reloadTree();
        } else {
            let path = changes.path ? changes.path.currentValue : [];
            if (path.length > 0 && this.treeData != null) {
                this.expandPath();
            } else {
                this.selectedNodes = [];
            }
        }
    }

    // Recurses down the tree, expanding nodes identified in the path array of folder id's
    // Sets the last node in the path as selected
    expandPath(tree: TreeNode[] = this.treeData, path: string[] = Object.assign([], this.path)) {
        let key = path.shift();
        let isLastNodeInPath = path.length == 0;
        if (key) {
            //Logging.all("Tree.expandPath", 'PATH', path, 'KEY', key, isLastNodeInPath, 'TREE', tree);
            for (let node of tree) {
                if (node.key == key) {
                    //console.log(`       --> ${node.key}`);
                    node.expanded = true;
                    if (isLastNodeInPath) {
                        // select it since it's the last one
                        this.selectedNodes = [];
                        this.selectedNodes.push(node);
                    }
                    this.expandPath(node.children, path);
                    break;
                }
            }
        }
    }

    // Enables/Disables context menu items based on permissions 
    handleShowContextMenu() {

        let canView = true, canEdit = true, canDelete = true, canBuildFolders = true, canBuildFormInstances = true;
        let canCreateFolder = true, canCreateForm = true;
        // set flags to false if _any_ of the selected nodes have the flag set to false
        // this is so that when multiple nodes are selected, a menu option will only be available
        // if ALL the selected nodes grant the user permissions to perform an operation
        for (let node of this.selectedNodes) {
            if (!node.data.canView) canView = false;
            if (!node.data.canEdit) canEdit = false;
            if (!node.data.canDelete) canDelete = false;
            if (!node.data.canBuildFolders) canBuildFolders = false;
            if (!node.data.canBuildFormInstances) canBuildFormInstances = false;

            //simplified permission model
            if (!node.data.canCreateFolder) canCreateFolder = false;
            if (!node.data.canCreateForm) canCreateForm = false;
        }

        let noFoldersSelected = this.selectedNodes.filter(x => { return x.type == TreeConstants.FOLDER_NODE }).length == 0;

        // Loop the contextMenuItems and disable options as needed
        for (let item of this.contextMenuItems) {

            if (this.opIsRename(item)) {
                item.disabled = !this.userCanRenameSelectedNodeType(noFoldersSelected, canBuildFolders, canBuildFormInstances, canEdit);
            }
            if (this.opIsDelete(item)){
                item.disabled = !canDelete;
            }
            if (this.opIsCutOrCopy(item)) {
                item.disabled = !this.userCanCopyThisNode(noFoldersSelected, canBuildFolders, canBuildFormInstances);
            }
            if (this.opIsPaste(item)) {
                item.disabled = !this.userCanPasteIntoThisNode(noFoldersSelected, canBuildFolders, canBuildFormInstances);
            }

            if (this.isPasteOption(item)) {
                if (noFoldersSelected) {
                    item.visible = false;
                } else {
                    item.visible = true;
                    if (!(this.clipboardService.hasCopiedItemsOfType([ItemTypeEnum.FORM_INSTANCE, ItemTypeEnum.FOLDER]))
                        && this.cutNodes?.length == 0) {
                        item.disabled = true;
                    }
                    if (this.clipboardService.AllCopiedAreReferences) {
                        item.label = TreeConstants.PASTE_ORIGINAL;
                    } else {
                        item.label = TreeConstants.PASTE;
                    }
                }
            }

            if (this.isPasteReferenceOption(item)) {
                if (noFoldersSelected) {
                    item.visible = false;
                } else {
                    item.visible = true;
                    if (!(this.clipboardService.hasCopiedItemsOfType([ItemTypeEnum.FORM_INSTANCE]))) {
                        item.disabled = true; //no copied items to paste reference for
                    }
                    if (this.clipboardService.AllCopiedAreReferences) {
                        item.label = TreeConstants.PASTE_ANOTHER_REFERENCE;
                    } else {
                        item.label = TreeConstants.PASTE_FORM_REFERENCE;
                    }
                    if (this.cutNodes?.length > 0) {
                        item.disabled = true; //can't paste reference to cut objects
                    }
                }
            }
        }
    }

    private userCanCopyThisNode(nodeIsFormInstance: boolean, canBuildFolders: boolean, canBuildFormInstances: boolean): boolean {
        if (nodeIsFormInstance) {
            return canBuildFormInstances;
        } else {
            return canBuildFolders;
        }
    }

    private userCanPasteIntoThisNode(nodeIsFormInstance: boolean, canBuildFolders: boolean, canBuildFormInstances: boolean): boolean {
        if (nodeIsFormInstance) return false;

        let copiedNodeType = this.copiedNodes?.length > 0 ? this.copiedNodes[0].type : null;
        let cutNodeType = this.cutNodes?.length > 0 ? this.cutNodes[0].type : null;
        let nodeType = copiedNodeType ?? cutNodeType;
        
        if (nodeType == null) {
            return false;
        } else if (nodeType == "FormInstance") {
            return canBuildFormInstances;
        } else {
            return canBuildFolders;
        }
    }

    private userCanRenameSelectedNodeType(nodeIsFormInstance: boolean, canBuildFolders: boolean, canBuildFormInstances: boolean, canEdit: boolean): boolean {
        if (nodeIsFormInstance) {
            return canEdit && canBuildFormInstances;
        } else {
            return canEdit && canBuildFolders;
        }
    }

    // resets menu options back to their default state of enabled
    handleHideContextMenu() {
        for (let item of this.contextMenuItems) {
            item.disabled = false;

        }
    }

    makeNodeEditable(event: any) {
        let node = this.selectedNodes[0];
        node.type = 'renameFolder'; // maps to ngTemplate in the html template
        node.draggable = false;     // without this, cannot select text in the node
        node.droppable = false;
        setTimeout(() => {
            $('#renameTreeNodeInput').select();
        }, 100);
    }

    doRename(event: any, node: TreeNode) {
        let entity = this.mapNodeToEntity(node);
        if (entity instanceof Folder) {
            this.folderService.update(entity);
        } else {
            this.formInstanceService.rename(entity);
        }
        node.type = this.getNodeType(node); // reset type so the correct template is applied in the html
        node.draggable = true;
        node.droppable = true;
        this.nodeSelected.emit(node);
    }

    deleteNode(event: any) {
        let n = this.selectedNodes[0];
        if (this.isFolder(n)) {
            this.deleteOnConfirm(TreeConstants.FOLDER_NODE, () => {
                this.folderService.delete(n.data.id).then(x => {
                    this.deleteNodeFromUI(event, n);
                    this.reloadTree();
                });
            });
        } else {
            this.deleteOnConfirm(TreeConstants.FORM_NODE, () => {
                // TODO -- pass all node ids to the backend to they can be deleted enmassee
                for (let n of this.selectedNodes) {
                    this.formInstanceService.delete(n.data.id).then(x => {
                        this.deleteNodeFromUI(event, n);
                        if (n.data.isReferenced) {
                            // if the form instance being deleted is referenced by other form instances, need to reload the tree to make sure references get synced with backend data
                            this.reloadTree();
                        }

                    });
                }
            });
        }
    }

    // Populates the this.copiedNodes array
    copySelected() {
        this.copiedNodes = null;
        this.cutNodes = null;
        this.initCutCopy(TreeConstants.COPY);
        this.progressIndicatorService.message(`Copied ${this.selectedNodes.map(n => { return `"${n.label}"` }).join(', ')}`);
    }

    // Populates the this.cutNodes array
    cutSelected() {
        this.copiedNodes = null;
        this.cutNodes = null;
        this.initCutCopy(TreeConstants.CUT);
        this.progressIndicatorService.message(`Cut ${this.selectedNodes.map(n => { return `"${n.label}"` }).join(', ')}`);
    }

    pasteIntoSelectedFolder(event: any, createReferences: boolean): void {
        if (this.copiedNodes?.length > 0) {
            //paste copied items
            let copiedType: string;
            if (this.clipboardService.hasCopiedItemsOfType([ItemTypeEnum.FORM_INSTANCE, ItemTypeEnum.FOLDER])) {
                copiedType = this.clipboardService.CopiedItems[0].getType();
            }
            if (this.copyPasteStateIsInvalid(copiedType, createReferences)) { return; }

            if (copiedType == ItemTypeEnum.FOLDER) {
                this.pasteCopiedFolders();
            } else if (copiedType == ItemTypeEnum.FORM_INSTANCE) {
                this.pasteFormInstances(event, createReferences);
            }
        }

        if (this.cutNodes?.length > 0) {
            //paste cut items
            let cutType: string;
            if (this.clipboardService.hasCutItemsOfType([ItemTypeEnum.FORM_INSTANCE, ItemTypeEnum.FOLDER])) {
                cutType = this.clipboardService.CutItems[0].getType();
            }
            if (this.copyPasteStateIsInvalid(cutType, createReferences)) { return; }

            if (cutType == ItemTypeEnum.FOLDER) {
                this.pasteCutFolders();
            } else if (cutType == ItemTypeEnum.FORM_INSTANCE) {
                let targetFolder = this.selectedNodes[0];
                this.cutNodes.forEach(cutNode => {
                    this.folderService.moveFormInstance(cutNode.data.id, targetFolder.data.id).then(x => {
                        this.removeNodeFromParent(cutNode);
                        this.addNode(cutNode, targetFolder);
                    });
                });
            }
        }
    }

    public get IsLoading(): boolean {
        return this.isLoading;
    }

    public get NoData(): boolean {
        return this.noData;
    }

    public get TreeData(): TreeNode[] {
        return this.treeData;
    }

    public get SelectedNodes(): TreeNode[] {
        return this.selectedNodes;
    }

    public set SelectedNodes(val: TreeNode[]) {
        this.selectedNodes = val;
    }

    public get PinnedOpen(): boolean {
        return this.pinnedOpen;
    }
    public set PinnedOpen(val: boolean) {
        this.pinnedOpen = val;
    }

    public get IncludeForms(): boolean {
        return this.includeForms;
    }

    public set IncludeForms(val: boolean) {
        this.includeForms = val;
    }

    public get PinOutlineClass(): string {
        return this.pinOutlineClass;
    }

    public get IncludeFormsOutlineClass(): string {
        return this.includeFormsOutlineClass;
    }

    public get IncludeFormsButtonIcon(): string {
        return this.includeForms ? 'folder' : 'article';
    }

    private opIsCutOrCopy(item: MenuItem): boolean {
        return item.id == TreeConstants.COPY || item.id == TreeConstants.CUT;
    }

    private opIsPaste(item: MenuItem): boolean {
        let is: boolean = item.id == TreeConstants.PASTE || item.id == TreeConstants.PASTE_FORM_REFERENCE;
        
        return is;
    }

    private isPasteOption(item: MenuItem): boolean {
        return item.id == TreeConstants.PASTE;
    }

    private isPasteReferenceOption(item: MenuItem): boolean {
        return item.id == TreeConstants.PASTE_FORM_REFERENCE;
    }

    private opIsDelete(item: MenuItem): boolean {
        return item.id == TreeConstants.DELETE;
    }

    private opIsRename(item: MenuItem): boolean {
        return item.id == TreeConstants.RENAME;
    }

    private pasteCutFolders(): void {
        let targetFolder = this.selectedNodes[0];
        let progress = 10;
        this.progressIndicatorService.updateProgress(progress, progress, "Making Copy of Folder...");
        this.folderService.moveFolders(this.cutNodes.map(node => { return node.data.id }), targetFolder.data.id).then(job => {
            this.reloadTree();
            this.selectNode(targetFolder);
            this.progressIndicatorService.updateProgress(100, 100, "Folder(s) moved.");
        });
    }

    private pasteCopiedFolders(): void {
        let targetFolder = this.selectedNodes[0];

        let progress = 10;
        let totalTimeSpent = 0;
        this.progressIndicatorService.updateProgress(progress, progress, "Making Copy of Folder...");
        this.folderService.copyFolders(targetFolder.data.id, this.copiedNodes.map(node => { return node.data.id })).then(job => {
            // poll for a completed job by using rxjs's interval()

            interval(2500)          // runs every 2 1/2 seconds
                // pipe() chains a number of functions together
                .pipe(
                    /*
                     * switchMap() switches the source observable (job) to the
                     * observable returned by calling this.jobService.get(job.id)
                     * (See https://rxjs-dev.firebaseapp.com/api/operators/switchMap)
                     */
                    switchMap(() => this.jobService.getJob(job.jobKey)),
                    /*
                     * tap() performs a side effect for every emission on the source Observable, but returns an Observable
                     * that is identical to the source. Used here to update progress.
                     */
                    tap(() => {
                        let msg = "Making Copy of Folder";
                        // don't really know how long this will take since it's async, but mimic progress in 10% increments
                        if (progress == 100) {
                            progress = 0;
                        }
                        progress += 10;

                        totalTimeSpent += 10;
                        if (totalTimeSpent > 30) {
                            msg = `${msg} (this can take a while if there are lots of objects and/or attachments to copy)`;
                        }

                        this.progressIndicatorService.updateProgress(progress, (progress + 10), `${msg} ...`);
                    }),
                    // filer() filters out and responses based on provided condition
                    filter(job => (job.status == AsyncJob.STATUS_COMPLETE || job.status == AsyncJob.STATUS_COMPLETE_ERROR)),
                    /*
                     * take() "takes" a response that matched the filter condition and it then completes
                     * in this case it just takes the first such response and completes, causing polling
                     * to stop (See https://rxjs-dev.firebaseapp.com/api/operators/take)
                     */
                    take(1),
                    /*
                     * timeout() checks how long this whole polling thing has been going on for
                     * this won't be reached if take() completed, it'll only be reached if we didn't get
                     * a job with status of completed
                     */
                    timeout(120000), // give it a couple of minutes - if there are attachments this could take a while
                    catchError(error => of(job)) // read of() as "create an observable out of..."
                )
                .subscribe(
                    // the above polling done by the call to interval() has completed so we get
                    // the result of that, which either by an AsyncJob
                    job => {
                        if (job.status == AsyncJob.STATUS_COMPLETE_ERROR) {
                            this.progressIndicatorService.updateProgress(100, 100, "Encountered an error.");
                            this.errorAlert(
                                `There was a problem copying the Folder. Try reloading the tree in a few moments.`,
                                job.stdError);
                        } else {
                            this.reloadTree();
                            this.selectNode(targetFolder);
                            this.progressIndicatorService.updateProgress(100, 100, "Folder copied.");
                        }
                    }
                );
        });
    }

    private errorAlert(message: string, error: string) {
        let additionalInfo = null;
        let correlationId = error.split("Correlation ID = ")[1];
        this.dialog.open(AlertDialogComponent, {
            disableClose: true,
            maxWidth: "600px",
            data: new AlertDialogModel("An error occurred", message, additionalInfo, correlationId)
        });
    }

    private infoAlert(title: string, message: string) {
        this.dialog.open(AlertDialogComponent, {
            maxWidth: "600px",
            data: new AlertDialogModel(title, message)
        });
    }


    private pasteFormInstances(event: any, createReferences: boolean): void {
        let copiedFormInstances: FormInstance[] = this.clipboardService.CopiedItems as FormInstance[]
        let firstSelectedNode = this.selectedNodes[0];
        let folderId = null;
        if (this.isFolder(firstSelectedNode)) {
            folderId = firstSelectedNode.data.id;
            firstSelectedNode.expanded = true;
        }
        else {
            folderId = firstSelectedNode.parent.data.id;
        }
        this.folderService.copyFormInstances(folderId, copiedFormInstances, createReferences).then(newReferences => {
            this.reloadTree();
            this.selectNode(firstSelectedNode);
        });
    }

    public getListItemsFromNodes(): IListItem[] {
        return this.selectedNodes.map((x: TreeNode) => (this.mapToListItem(x)));
    }

    private mapToListItem(x: TreeNode): IListItem {
        let retVal: IListItem;

        if (x.type.toUpperCase() === ItemTypeEnum.FOLDER.toUpperCase()) {
            let folder: Folder = new Folder();

            folder.name = x.label;
            folder.setId(x.data.id);

            retVal = folder;
        }

        if (x.type.toUpperCase() === ItemTypeEnum.FORM_INSTANCE_NO_DASH.toUpperCase()) {
            let formInstance: FormInstance = new FormInstance();

            formInstance.name = x.label;
            formInstance.setId(x.data.id);

            retVal = formInstance;
        }

        return retVal;
    }

    public handleContextMenuChoice(e: any) {
        //console.log({ inContext: e, selectedNodes: this.selectedNodes });
        if (e.item.label == TreeConstants.EXPAND_ALL) {
            this.selectedNodes.forEach(node => {
                this.expandCollapseAll(node, true);
            });
        } else if (e.item.label == TreeConstants.COLLAPSE_ALL) {
            this.selectedNodes.forEach(node => {
                this.expandCollapseAll(node, false);
            });
        }
    }

    public expandCollapseAll(node: TreeNode, expand: boolean) {
        node.expanded = expand;
        node.children.forEach(node => {
            this.expandCollapseAll(node, expand);
        });
    }

    public nodeSelect(e: any) {
        let node = e.node;
        let nodeData = node.data;
        let type = nodeData.type;

        //if (this.selectedNodes[0] && this.selectedNodes[0].data.type != type) {
        //    alert("You can only select nodes of the same type -- Folders OR Forms.");
        //    // deselect the node just selected.
        //    this.selectedNodes = this.selectedNodes.filter(n => { return n.data.type != type });
        //    return;
        //}

        if (this.selectedNodes.length == 1) {
            let eventData = {
                originalEvent: e.originalEvent,
                item: { getType: () => type, id: nodeData.id, parentId: nodeData.parentId },
                tree: this.tree
            };
            this.nodeSelected.emit(eventData);
        }
        let selectedNode = this.selectedNodes[this.selectedNodes.length - 1];
        if (selectedNode) selectedNode.expanded = true;
    }

    public nodeExpand(e: any) {
        //console.log({ nodeExpand: e });
    }

    public onDrop(event: any): void {
        let draggedNode: TreeNode = event.dragNode;
        let targetNode: TreeNode = event.dropNode;
        let isTopLevel = !targetNode.parent;

        if (this.droppedBetweenNodes(event)) {
            let parentId = targetNode.data.parentId;
            if (this.reorderAtSameLevel(draggedNode, targetNode)) {

                event.accept();
                let newIdsAndPositions = this.getNewIdsAndPositions(targetNode, draggedNode);

                UtilityHelper.runWhenStackClear(() => {
                    if (this.isFolder(targetNode)) {
                        this.folderService.reorderChildFolders(newIdsAndPositions, parentId)
                            .then(res => {
                                isTopLevel ? this.reloadTree(true) : this.selectNode(targetNode.parent, event.originalEvent);
                            });
                    } else {
                        this.folderService.reorderFormInstances(newIdsAndPositions, parentId)
                            .then(res => {
                                isTopLevel ? this.reloadTree(true) : this.selectNode(targetNode.parent, event.originalEvent);
                            });
                    }
                });
            }
            else { // reordering at a different level -- need to move _and_ set the order
                let newParentFolderName = isTopLevel ? "The Top Level" : targetNode.parent.label;
                this.moveTreeNodes([draggedNode], draggedNode, targetNode, newParentFolderName, event, parentId, isTopLevel);
            }
        } else {
            let parentId = targetNode.data.id;
            let destName = targetNode.label;

            //ignore if dragged node is already in the destination folder
            if (draggedNode.parent && draggedNode.parent.data.id == targetNode.data.id) return;

            //if target is a form instance, change target to the containing folder
            if (targetNode.data.type == "FormInstance") {
                if (targetNode.parent) {
                    targetNode = targetNode.parent;
                }
                else {
                    targetNode = this.tree;
                    destName = "The Top Level";
                }
            }

            // pharvey - change on 1/19/2023 - instead of passing just targetNode, pass all selectedNodes as they are what have been dragged
            // UNLESS selectedNodes is empty, in which case use draggedNode. (It's possible to drag a node without selecting it')
            let allDraggedNodes = this.selectedNodes.length > 1 ? this.selectedNodes : [draggedNode];
            this.moveTreeNodes(allDraggedNodes, draggedNode, targetNode, destName, event, parentId, isTopLevel);
        }
    }

    private getNewIdsAndPositions(targetNode: TreeNode<any>, draggedNode: TreeNode<any>) {
        let newIdsAndPositions = {}, newNamesAndPositions = {};
        let i = 0;

        let children = this.getChildren(targetNode);
        children.forEach(node => {
            let nodeType = node.data ? node.data.type : node.type;
            let nodeId = node.data ? node.data.id : node['id'];
            if (nodeType == draggedNode.data.type) {
                newIdsAndPositions[nodeId] = i++;
                newNamesAndPositions[node.label] = i++;
            }
        });
        return newIdsAndPositions;
    }

    private moveTreeNodes(draggedNodes: TreeNode[], draggedNode: TreeNode, targetNode: TreeNode<any>, newParentFolderName: string, event: any, parentId: number, isTopLevel: boolean) {
        this.confirmTreeMove(draggedNodes, newParentFolderName, () => {
            event.accept();
            let newIdsAndPositions = this.getNewIdsAndPositions(targetNode, draggedNode);
            if (this.isFolder(draggedNode)) {
                UtilityHelper.runWhenStackClear(() => {
                    this.folderService.moveFolders([draggedNode.data.id], parentId).then(x => {
                        this.folderService.reorderChildFolders(newIdsAndPositions, parentId)
                            .then(res => {
                                this.reloadTree(true);
                                this.selectNode(targetNode, event.originalEvent);
                            });
                    });
                });
            } else {
                this.folderService.moveFormInstances([draggedNode.data.id], parentId).then(x => {
                    this.folderService.reorderFormInstances(newIdsAndPositions, parentId)
                        .then(res => {
                            this.reloadTree(true);
                            this.selectNode(targetNode, event.originalEvent);
                        });
                });
            }
        });
    }


    private getChildren(node: TreeNode<any>): TreeNode[] {
        let children: TreeNode[]

        if (node.parent) {
            children = node.parent.children;
        } else {
            children = this.treeData;
        }
        return children;
    }

    private reorderAtSameLevel(draggedNode: TreeNode<any>, targetNode: TreeNode<any>) {
        return this.reorderAtRootFolderLevel(draggedNode, targetNode) || this.reorderInNonRootFolder(draggedNode, targetNode);
    }
    private reorderInNonRootFolder(draggedNode: TreeNode<any>, targetNode: TreeNode<any>) {
        return draggedNode.parent == targetNode.parent;
    }

    private reorderAtRootFolderLevel(draggedNode: TreeNode<any>, targetNode: TreeNode<any>) {
        return !targetNode.parent && !draggedNode.parent;
    }

    // returns true if drop target is the bar that appears between nodes (https://forum.primefaces.org/viewtopic.php?t=67224)
    private droppedBetweenNodes(event: any) {
        return event.originalEvent.target.className.match(/p-treenode-droppoint-active/);
    }

    private isFolder(targetNode: TreeNode) {
        return targetNode.data.type == TreeConstants.FOLDER_NODE;
    }

    private selectNode(node: TreeNode, event: any = { originalEvent: null }) {
        this.selectedNodes = [];
        this.selectedNodes.push(node);
        this.nodeSelected.emit({
            originalEvent: event.originalEvent,
            item: { getType: () => node.data.type, id: node.data.id, parentId: node.data.parentId }
        });
    }

    public onClose(): void {
        if (this.pinnedOpen) this.togglePinnnedOpen();
        this.folderFormInstanceTreeService.ShowTree = false;
    }

    public getClassForPermissionsDisplay(node: TreeNode): string {
        if (!node.data.canView) {
            return "treeNodeNotAccessibleToSelectedUser"
        } else {
            return "treeNodeAccessibleToSelectedUser";
        }
    }

    public getCanClass(flag: boolean): string {
        if (flag) {
            return 'can-do';
        } else {
            return 'cannot-do';
        }
    }

    public nextMode(): string {
        return this.mode == TreeConstants.REGULAR_MODE ? TreeConstants.PERMISSIONS_MODE : TreeConstants.REGULAR_MODE;
    }

    public toggleMode(): void {
        this.mode = this.nextMode();
        this.modeClass = this.mode == TreeConstants.PERMISSIONS_MODE ? 'permissions-mode' : 'regular-mode';
        this.treeData = this.rawTreeToTreeNodes(this.rawFolderFormInstanceTree);
        if (this.mode == TreeConstants.PERMISSIONS_MODE && this.membersForPermissionsLookup == null) {
            this.roleService.getFolderRoleGroupMembers(this.rootFolderId).then(members => {
                this.membersForPermissionsLookup = members;
            });
        }
    }

    public displayPermissionsLookup(): boolean {
        return this.mode == TreeConstants.PERMISSIONS_MODE && this.membersForPermissionsLookup != null;
    }

    public togglePinnnedOpen(): void {
        this.pinOutlineClass = this.pinOutlineClass == TreeConstants.ICON_SOLID_CLASS ? TreeConstants.ICON_OUTLINE_CLASS : TreeConstants.ICON_SOLID_CLASS;
        // pharv - vnext-521 - to decouple this component from structure, instead of emiting an event which
        // structure has to handle, tage care of it here via the tree service 
        this.folderFormInstanceTreeService.toggleTreePinnedState();
    }

    public toggleIncludeForms(): void {
        this.includeFormsOutlineClass = this.includeFormsOutlineClass == TreeConstants.ICON_SOLID_CLASS ? TreeConstants.ICON_OUTLINE_CLASS : TreeConstants.ICON_SOLID_CLASS;
        let include = this.browserStorageService.get(this.includeFormsKeyForStorage);
        if (include == null || include == 'false') {
            this.includeForms = true;
            this.browserStorageService.set(this.includeFormsKeyForStorage, 'true');
        } else {
            this.includeForms = false;
            this.browserStorageService.set(this.includeFormsKeyForStorage, 'false');
        }

        this.reloadTree(true);
    }

    public setToggleButtonStates(): void {
        let pinOpen = this.browserStorageService.get(BrowserStorageEnums.PINTREEOPEN);
        if (pinOpen != null && pinOpen == 'true') {
            this.pinnedOpen = true;
        }

        let includeForms = this.browserStorageService.get(`${BrowserStorageEnums.INCLUDE_FORMS_IN_TREE}_${this.site.id}`);
        if (includeForms != null && includeForms == 'true') {
            this.includeForms = true;
        }

        this.pinOutlineClass = this.pinnedOpen ? TreeConstants.ICON_SOLID_CLASS : TreeConstants.ICON_OUTLINE_CLASS;
        this.includeFormsOutlineClass = this.includeForms ? TreeConstants.ICON_SOLID_CLASS : TreeConstants.ICON_OUTLINE_CLASS;
    }

    public reloadTree(showProgress: boolean = false) {
        this.folderFormInstanceTreeService.getTree(this.site.id, this.includeForms, showProgress).then(response => {
            if (response.childFolders == null) {
                this.noData = true;
            } else {
                this.noData = false;
            }
            this.filterFormInstances(response);
            this.expandPath();
        });
    }

    // reloads the tree with information about the selected user's permissions on each node
    public refreshTreeWithPermissions(): void {
        this.refreshingTreeWithPermissions = true;
        this.folderFormInstanceTreeService.getTreeFor(this.site.id, this.permissionsLookupValue.split('|')[0]).then(res => {
            //this.rawFolderFormInstanceTree = res;
            //this.treeData = this.rawTreeToTreeNodes(this.rawFolderFormInstanceTree);

            this.filterFormInstances(res);

            this.showingPermissionsFor = this.permissionsLookupValue;
            this.refreshingTreeWithPermissions = false;
        });
    }

    public showVisibleNodes(event: any) {
        // When primeng tree supports programmatic filtering of nodes
        // implement here showing by filtering by 'viewable'
    }

    //Kevin
    public itemsDropped(event: any, node: TreeNode) {

        if (event.dragData.folderName != node.label) {
            this.dragDropService.getItems(event).then((response) => {
                let droppedItems: IDragDropItem[] = response as IDragDropItem[];

                let nodeToItem: IListItem = {
                    name: node.label,
                    getType: () => node.data.type,
                    getId: () => node.data.id
                } as IListItem

                let droppedItemsToIItems = droppedItems.map((droppedItem) => {
                    return {
                        name: droppedItem.label,
                        id: droppedItem.id,
                        getId: () => droppedItem.id,
                        getType: () => droppedItem.type,
                        getParentId: () => node.data.id
                    } as IItem;
                });

                this.onDragDrop(event, nodeToItem, droppedItemsToIItems);
            });
        }
    }

    // private helpers

    // presents an error message and returns true if copy/paste state is invalid
    private copyPasteStateIsInvalid(copiedType: string, createReferences: boolean): boolean {
        if (copiedType == null) {
            this.infoAlert("No nodes have been selected", "Please copy or cut nodes before pasting.");
            return true;
        }
        if (this.selectedNodes.length > 1) {
            this.infoAlert("More than one folder is selected", "Paste into one folder at a time.");
            return true;
        }
        if (this.selectedNodes[0].data.type != TreeConstants.FOLDER_NODE) {
            this.infoAlert("Unable to paste into a form", "Please paste into a folder.");
            return true;
        }
        if (createReferences && copiedType == TreeConstants.FOLDER_NODE) {
            this.infoAlert("Unable to paste a reference to a folder", "References can only be created to forms.");
            return true;
        }
        if (createReferences && copiedType == TreeConstants.FORM_NODE) {
            let references = this.copiedNodes.filter(x => x.data.referencedFormInstanceId != null);
            if (references.length > 0) {
                this.infoAlert("Unable to paste a reference to form reference", "References can only be created to forms.");
                return true;
            }
        }
        return false;
    }

    private deleteNodeFromUI(event: any, node: TreeNode) {
        let parent = node.parent;
        if (parent) {
            let index = node.parent.children.indexOf(node);
            node.parent.children.splice(index, 1);

            this.nodeSelected.emit({
                originalEvent: event.originalEvent,
                item: { getType: () => TreeConstants.FOLDER_NODE, id: parent.data.id, parentId: parent.data.parentId }
            });
        } else {
            this.nodeSelected.emit({
                originalEvent: event.originalEvent,
                item: null
            });
        }
    }

    // Converts the tree data from the server to nested TreeNode objects used to
    // populate the PrimeNG tree
    private rawTreeToTreeNodes(tree: FolderFormInstanceTree, json: TreeNode[] = []): any {
        if (tree.parentFolderId == null) {
            this.rootFolderId = tree.id;
        }
        if ((tree.childFolders !== null) && (tree.childFolders.length > 0)) {
            this.buildTreeNode(tree.childFolders, json);
        }
        if ((tree.childFormInstances !== null) && (tree.childFormInstances.length > 0)) {
            this.buildTreeNode(tree.childFormInstances, json);
        }
        return json;
    }

    private buildTreeNode(treeNodes: FolderFormInstanceTree[], json: TreeNode[]) {
        for (let treeNode of treeNodes) {
            let node = {
                label: `${treeNode.name}`,
                icon: this.getIcon(treeNode),
                //type: treeNode.type,
                data: {
                    id: treeNode.id,
                    name: treeNode.name,
                    parentId: treeNode.parentFolderId,
                    type: treeNode.type,
                    referencedFormInstanceId: treeNode.referencedFormInstanceId,
                    isReferenced: treeNode.isReferenced,
                    canView: treeNode.canView,
                    canEdit: treeNode.canEdit,
                    canDelete: treeNode.canDelete,
                    canGrant: treeNode.canGrant,
                    canBuildFolders: treeNode.canBuildFolders,
                    canBuildFormInstances: treeNode.canBuildFormInstances,
                    canBuildForms: treeNode.canBuildForms,
                    canBuildWorkflows: treeNode.canBuildWorkflows
                },
                children: [],
                childFolders: [],
                childFormInstances: [],
                key: treeNode.qualifiedId()
            };

            node['type'] = this.getNodeType(node); // see p-Template references in the html which style nodes based on type

            if (this.mode == TreeConstants.PERMISSIONS_MODE) {
                if (treeNode.canView) {
                    // This gets added to the searchable fields so user can search for viewable nodes
                    // (see filterBy attribute of <p-tree> in the template)
                    node.data['searchable'] = 'viewable,visible';
                }
            }
            json.push(node);
            this.rawTreeToTreeNodes(treeNode, node.children);
        }
    }

    // helper methods
    private getNodeType(treeNode: TreeNode): string {
        if (this.mode == TreeConstants.PERMISSIONS_MODE) {
            return 'permissionsMode';
        }
        else if (treeNode.data.referencedFormInstanceId) {
            return TreeConstants.FORM_REF_NODE;
        } else if (treeNode.data.isReferenced) {
            return TreeConstants.REFERENCED_FORM_NODE;
        }
        else {
            return treeNode.data.type;
        }
    }

    public getIcon(folderFormInstance: FolderFormInstanceTree): string {
        let iconStyle = 'exit_to_app';
        if (folderFormInstance.isReferenced) {
            iconStyle = 'article';
            if (folderFormInstance.isReferencedCount > 0) {
                iconStyle = 'filter_' + folderFormInstance.isReferencedCount.toString();
            }
            if (folderFormInstance.isReferencedCount > 9) {
                iconStyle = 'filter_9_plus';
            }
        }
        return iconStyle;
    }

    private static getTreeHtml(tree: FolderTree): string {
        let strHtml: string = `<span>${tree.name} (${tree.id})</span>`;

        if ((tree.childFolders !== null) && (tree.childFolders.length > 0)) {
            strHtml += "\n\n<ul>";

            for (let iChild: number = 0; iChild < tree.childFolders.length; iChild++) {
                let child: FolderTree = tree.childFolders[iChild];

                let strChildHtml = FolderTreeComponent.getTreeHtml(child);

                strHtml += "<li>" + strChildHtml + "</li>";
            }

            strHtml += "</ul>\n";
        }

        return (strHtml);
    }

    // Reinitializes cutNodes and copiedNodes
    private initCutCopy(menuOption: string) {
        this.copiedNodes = null;
        this.cutNodes = null;
        if (menuOption === TreeConstants.CUT) {
            this.copiedNodes = [];
            this.cutNodes = this.selectedNodes.map(n => { return Object.assign({}, n) });
            let items = [];
            for (let node of this.cutNodes) {
                if (node.type == TreeConstants.FOLDER_NODE) {
                    let folder = new Folder();
                    folder.id = node.data.id;
                    folder.name = node.data.name;
                    items.push(folder);
                } else if (node.type == TreeConstants.FORM_NODE) {
                    let fi = new FormInstance();
                    fi.isReference = (node.type.valueOf() == TreeConstants.FORM_REF_NODE);
                    fi.id = node.data.id;
                    fi.name = node.data.name;
                    items.push(fi);
                }
            }

            this.clipboardService.cutItemsToClipboard(items);
        } else if (menuOption === TreeConstants.COPY) {
            this.cutNodes = [];
            this.copiedNodes = this.selectedNodes.map(n => { return Object.assign({}, n) });
            // 12/15/2021 ~ This is new code which harnesses the new clipboard service which both the tree and the listviews can use
            let items = [];
            for (let node of this.copiedNodes) {
                if (node.type == TreeConstants.FOLDER_NODE) {
                    let folder = new Folder();
                    folder.id = node.data.id;
                    folder.name = node.data.name;
                    items.push(folder);
                } else if (node.type == TreeConstants.FORM_NODE || node.type == TreeConstants.FORM_REF_NODE || node.type == TreeConstants.REFERENCED_FORM_NODE) {
                    let fi = new FormInstance();
                    fi.isReference = (node.type.valueOf() == TreeConstants.FORM_REF_NODE);
                    fi.id = node.data.id;
                    fi.name = node.data.name;
                    items.push(fi);
                }
            }
            this.clipboardService.copyItemsToClipboard(items);
        }
    }

    //Given a TreeNode, returns either a Folder or FormInstance object
    private mapNodeToEntity(node: TreeNode): any {
        let id = node.data.id;
        let name = node.label;
        let dataCollectionId = this.site.id;
        let parentId = node.data.parentId;

        if (node.key.startsWith(TreeConstants.FOLDER_NODE)) {
            let folder = new Folder();
            folder.id = id;
            folder.name = name;
            folder.dataCollectionId = dataCollectionId;
            folder.parentFolderId = parentId;
            return folder;
        } else if (node.key.startsWith(TreeConstants.FORM_NODE)) {
            let form = new FormInstance();
            form.id = id;
            form.name = name;
            form.folderId = parentId;
            if (node.data.referencedFormInstanceId != null) {
                let referenced = new FormInstance();
                referenced.id = node.data.referencedFormInstanceId;
                referenced.name = name;
                form.referencedFormInstance = referenced;
            }
            return form;
        }
    }

    // key is form <entity>_123
    private getIdFromKey(key: string): number {
        return parseInt(key.split('_')[1]);
    }

    private confirmTreeMove(movedNodes: TreeNode[], destinationName: String, callback: Function) {
        let entityTypeDesc = DragDropEnum.FORM;
        if (movedNodes[0].type == DragDropEnum.FOLDER) {
            entityTypeDesc = DragDropEnum.FOLDER;
        }
        let dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: new ConfirmationDialogModel(
                `Move ${entityTypeDesc}`,
                `Are you sure you want to move the ${entityTypeDesc}(s) "${movedNodes.map(x => x.label).join(', ')}" into Folder "${destinationName}"?`)
        });
        dialogRef.afterClosed().subscribe(yes => {
            if (yes) {
                callback();
            }
        });
    }

    private removeNodeFromParent(node: TreeNode) {
        if (node.parent) { //remove from folder
            node.parent.children = node.parent.children.filter(n => { return n.key != node.key });
        }
        else { //remove form from root
            this.tree.childFormInstances = this.tree.childFormInstances.filter(function (el) { return el.id == node.data.id; });
        }
    }

    private addNode(nodeToAdd: TreeNode, parentNode: TreeNode) {
        parentNode.children.push(nodeToAdd);
        parentNode.expanded = true;
    }

    private deleteOnConfirm(entity: string, callback: Function) {
        let dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: new ConfirmationDialogModel(`Delete ${entity}`, `Are you sure you want to delete this ${entity}? The operation cannot be undone.`)
        });
        dialogRef.afterClosed().subscribe(yes => {
            if (yes) callback();
        });
    }
}
